"use client"

import { useCart } from "@/lib/context/cart-context"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"
import { getCheckoutStep } from "@/lib/util/get-checkout-step"
import { convertToLocale } from "@/lib/util/money"
import AppliedPromotions from "@/modules/cart/components/applied-promotions"
import ItemsTemplate from "@/modules/cart/templates/items"
import Button from "@/modules/common/components/button"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import FreeShippingPriceNudge from "@/modules/shipping/components/free-shipping-price-nudge"
import { StoreFreeShippingPrice } from "@/types/shipping-option/http"
import { CreditCardSolid, BoltSolid } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import { Drawer, Text } from "@medusajs/ui"
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation"
import { useMemo } from "react"

type CartDrawerContentProps = {
  customer: HttpTypes.StoreCustomer | null
  freeShippingPrices: StoreFreeShippingPrice[]
  /** Called before navigation so the drawer closes visibly; prevents users thinking the button did nothing */
  onClose?: () => void
}

export const CartDrawerContent = ({
  customer,
  freeShippingPrices,
  onClose,
}: CartDrawerContentProps) => {
  const { cart } = useCart()
  const router = useRouter()
  const params = useParams()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const countryCode = params?.countryCode as string | undefined

  const isOnExpressCheckout =
    pathname?.includes("/products/") && searchParams?.get("express") === "true"

  const firstProductHandle = cart?.items?.[0]?.product?.handle
  const expressCheckoutPath =
    countryCode && firstProductHandle
      ? `/${countryCode}/products/${firstProductHandle}?express=true`
      : null

  const handleExpressCheckoutClick = () => {
    captureAnalyticsEvent("checkout_started", {
      entry_point: "cart_drawer",
      checkout_type: "express",
    })
    onClose?.()
    if (isOnExpressCheckout) {
      return
    }
    if (expressCheckoutPath) {
      router.push(expressCheckoutPath)
    }
  }

  const items = cart?.items || []
  const promotions = cart?.promotions || []

  const totalItems =
    items?.reduce((acc, item) => {
      return acc + item.quantity
    }, 0) || 0

  const subtotal = useMemo(() => cart?.item_subtotal ?? 0, [cart])

  const checkoutStep = cart ? getCheckoutStep(cart) : undefined
  const checkoutPath = customer
    ? checkoutStep
      ? `/checkout?step=${checkoutStep}`
      : "/checkout"
    : "/account"

  return (
    <>
      <Drawer.Header className="flex self-center">
        <Drawer.Title>
          {totalItems > 0
            ? `V vaši košarici imate ${totalItems} izdelkov`
            : "Vaša košarica je prazna"}
        </Drawer.Title>
      </Drawer.Header>
      {promotions.length > 0 && (
        <div className="p-4">
          <AppliedPromotions promotions={promotions} />
        </div>
      )}
      <div className="flex flex-col gap-y-4 h-full self-stretch justify-between overflow-auto">
        {cart && cart.items && (
          <>
            <ItemsTemplate
              cart={cart}
              showBorders={false}
              showTotal={false}
            />
            <div className="flex flex-col gap-y-3 w-full p-4">
              {cart && freeShippingPrices && (
                <FreeShippingPriceNudge
                  variant="inline"
                  cart={cart as any}
                  freeShippingPrices={freeShippingPrices}
                />
              )}
              
              <div className="flex justify-between">
                <Text>Vmesna vsota (brez poštnine in DDV)</Text>
                <Text>
                  {convertToLocale({
                    amount: subtotal,
                    currency_code: cart?.currency_code,
                  })}
                </Text>
              </div>
              <div className="flex flex-col gap-y-2">
                <LocalizedClientLink href="/cart">
                  <Button
                    variant="secondary"
                    className="w-full"
                    size="large"
                  >
                    Ogled košarice
                  </Button>
                </LocalizedClientLink>
                {customer ? (
                  <LocalizedClientLink href={checkoutPath}>
                    <Button
                      className="w-full"
                      size="large"
                      disabled={!cart || cart.items.length === 0}
                      onClick={() => {
                        captureAnalyticsEvent("checkout_started", {
                          entry_point: "cart_drawer",
                          checkout_type: "standard",
                        })
                      }}
                    >
                      <span className="flex w-full justify-between items-center">
                        <span className="flex gap-x-2 items-center">
                          <CreditCardSolid />
                          Na blagajno
                        </span>
                      </span>
                    </Button>
                  </LocalizedClientLink>
                ) : (
                  <>
                    <Button
                      className="w-full"
                      size="large"
                      disabled={
                        !cart ||
                        cart.items.length === 0 ||
                        (!isOnExpressCheckout && !expressCheckoutPath)
                      }
                      onClick={handleExpressCheckoutClick}
                    >
                      <span className="flex w-full justify-center items-center gap-x-2">
                        <BoltSolid className="w-4 h-4" />
                        <span>Hiter nakup brez registracije</span>
                      </span>
                    </Button>
                    <LocalizedClientLink href={checkoutPath}>
                      <Button variant="secondary" className="w-full" size="large">
                        Prijavite se za blagajno
                      </Button>
                    </LocalizedClientLink>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
} 